import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Shield, Mail, Lock, User, Phone, MapPin, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const LOCATIONS = ['Accra', 'Kumasi', 'Tamale', 'Cape Coast', 'Takoradi', 'Tema', 'Ho', 'Sunyani', 'Bolgatanga', 'Koforidua'];

export default function Register() {
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '', location: 'Accra' });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Client-side validation
    if (!form.name || form.name.trim().length < 2) {
      toast.error('Please enter your name (at least 2 characters)');
      return;
    }
    if (!form.email || !form.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      const res = await API.post('/auth/register', form);
      login(res.data.token, res.data.user);
      toast.success('Account created! Welcome to AutoTrust.');
      // Small delay to ensure auth state has propagated before navigating
      setTimeout(() => navigate('/dashboard'), 100);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Registration failed. Please try again.';
      // Check if it's a duplicate email error
      if (errorMsg.toLowerCase().includes('already') || errorMsg.toLowerCase().includes('exists')) {
        toast.error('This email is already registered. Try logging in instead.', {
          action: {
            label: 'Login',
            onClick: () => navigate('/login')
          }
        });
      } else {
        toast.error(errorMsg);
      }
    }
    setLoading(false);
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Use /auth/callback route which is NOT protected, then redirect to dashboard after auth
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const update = (field, val) => setForm(f => ({ ...f, [field]: val }));

  return (
    <main data-testid="register-page" className="min-h-[80vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gold/10 mb-2">
            <Shield className="w-6 h-6 text-gold" />
          </div>
          <h1 className="text-2xl font-bold">Create Account</h1>
          <p className="text-sm text-muted-foreground">Join AutoTrust Ghana marketplace</p>
        </div>

        <div className="bg-card border border-border/50 rounded-xl p-6 space-y-6">
          <Button variant="outline" className="w-full h-12 gap-3 font-medium" onClick={handleGoogleLogin} data-testid="google-register-btn">
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            Continue with Google
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-card px-3 text-muted-foreground">or register with email</span></div>
          </div>

          <form onSubmit={handleRegister} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Kwame Asante" value={form.name} onChange={(e) => update('name', e.target.value)}
                  className="pl-10 h-12" data-testid="register-name" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input type="email" placeholder="you@example.com" value={form.email} onChange={(e) => update('email', e.target.value)}
                  className="pl-10 h-12" data-testid="register-email" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input type="password" placeholder="Min 6 characters" value={form.password} onChange={(e) => update('password', e.target.value)}
                  className="pl-10 h-12" data-testid="register-password" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder="+233..." value={form.phone} onChange={(e) => update('phone', e.target.value)}
                    className="pl-10 h-12" data-testid="register-phone" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Location</Label>
                <Select value={form.location} onValueChange={(v) => update('location', v)}>
                  <SelectTrigger className="h-12" data-testid="register-location">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCATIONS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 bg-gold text-black hover:brightness-110 font-semibold gap-2" data-testid="register-submit-btn">
              {loading ? 'Creating...' : 'Create Account'} <ArrowRight className="w-4 h-4" />
            </Button>
          </form>
        </div>

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="text-gold hover:underline font-medium" data-testid="register-login-link">Sign in</Link>
        </p>
      </div>
    </main>
  );
}
